import { get, has, find } from 'lodash';
import { createSelector } from 'reselect';
import {
  selectUserDomain,
  makeSelectAccountPerks,
  makeSelectIsPollfishAdmin,
  getSelectedAccount,
} from './shared-selectors';
import {
  USER_RESEARCHER,
  USER_ADMIN,
  USER_PUBLISHER,
  SHARED_RESOURCES,
} from './constants';

const makeSelectUserIsPublisher = createSelector(selectUserDomain, (user) =>
  has(user, 'userDetails.publisher'),
);

const makeSelectPublisherDetails = createSelector(selectUserDomain, (user) =>
  get(user, 'userDetails.publisher'),
);

const makeSelectIsProdegeUser = createSelector(selectUserDomain, (user) =>
  get(user, 'userDetails.isProdegeUser', false),
);

const selectUserTierDomain = createSelector(
  makeSelectAccountPerks,
  (perkMetadata) => get(perkMetadata, 'tier'),
);

const makeSelectUserDetails = createSelector(
  selectUserDomain,
  (substate) => substate.userDetails,
);

const makeSelectUserWhiteLabelling = createSelector(
  makeSelectAccountPerks,
  (perkMetadata) => get(perkMetadata, 'whiteLabelling', {}),
);

const makeSelectUserBigQuery = createSelector(
  makeSelectAccountPerks,
  (perkMetadata) => get(perkMetadata, 'bigQueryExport', false),
);

const makeSelectUserTeamAccountSeats = createSelector(
  makeSelectAccountPerks,
  (perkMetadata) => get(perkMetadata, 'teamAccount.seats', 0),
);
const makeSelectUserTranslations = createSelector(
  makeSelectAccountPerks,
  (perkMetadata) => get(perkMetadata, 'translations', false),
);

const makeSelectUserDownloadSummary = createSelector(
  makeSelectAccountPerks,
  (perkMetadata) => get(perkMetadata, 'downloadSummary', false),
);

const makeSelectUserAiReport = createSelector(
  makeSelectAccountPerks,
  (perkMetadata) => get(perkMetadata, 'aiResultsReport', false),
);

const makeSelectBilling = createSelector(selectUserDomain, (user) =>
  get(user, 'userDetails.billing', false),
);

const makeSelectUserAccounts = createSelector(selectUserDomain, (substate) =>
  get(substate, 'userDetails.accounts', []),
);

const makeSelectUserAccountEmails = createSelector(
  selectUserDomain,
  (substate) => {
    const account = getSelectedAccount(substate) || {};
    const userEmails = get(account, 'members', []).map(
      (member) => member.email,
    );

    return userEmails;
  },
);

const makeSelectUserAccountSelected = createSelector(
  selectUserDomain,
  (substate) => getSelectedAccount(substate) || {},
);

const makeSelectAccountHasAdvertisingId = createSelector(
  makeSelectAccountPerks,
  (perkMetadata) => get(perkMetadata, 'respondentId', false) === true,
);

const makeSelectHasPerk = (perkName) =>
  createSelector(makeSelectAccountPerks, (perkMetadata) =>
    get(perkMetadata, `${perkName}`, false),
  );

const makeSelectedAccountHasMobileOsPerk = createSelector(
  makeSelectAccountPerks,
  (perkMetadata) => {
    const osVersionPerk = get(perkMetadata, 'osVersionPerk', false);
    return osVersionPerk;
  },
);

const makeSelectedAccountHasThirdParty = createSelector(
  makeSelectAccountPerks,
  (perkMetadata) => {
    const pfThirdPartySurvey = get(perkMetadata, 'pfThirdPartySurvey', false);
    const hasRegularPerk = get(perkMetadata, 'regularSurvey', false);
    return pfThirdPartySurvey && hasRegularPerk;
  },
);

const makeSelectedAccountHasDistributionLink = createSelector(
  makeSelectAccountPerks,
  (perkMetadata) => {
    const distributionLinkPerk = get(
      perkMetadata,
      'distributionLinkPerk',
      false,
    );
    return distributionLinkPerk;
  },
);

const makeSelectedAccountHasRegularSurvey = createSelector(
  makeSelectAccountPerks,
  (perkMetadata) => {
    const regularSurvey = get(perkMetadata, 'regularSurvey', false);
    return regularSurvey;
  },
);

const makeSelectedAccountHasPeeqSurvey = createSelector(
  makeSelectAccountPerks,
  (perkMetadata) => {
    const peeqSurvey = get(perkMetadata, 'peeqSurvey', false);
    return peeqSurvey;
  },
);

const makeSelectedAccountHasSettingIrAndLoi = createSelector(
  makeSelectAccountPerks,
  (perkMetadata) => get(perkMetadata, 'allowSettingIrAndLoi', false),
);

const makeSelectedAccountHasTemplates = createSelector(
  makeSelectAccountPerks,
  (perkMetadata) => {
    const useSurveyTemplates = get(perkMetadata, 'useSurveyTemplates', false);
    return useSurveyTemplates;
  },
);

const makeSelectUserEmail = createSelector(selectUserDomain, (substate) => {
  const selectedAccount = getSelectedAccount(substate);
  const userEmail = get(selectedAccount, 'email', '');
  return userEmail;
});

const makeSelectUserRoles = createSelector(selectUserDomain, (substate) => {
  const accounts = get(substate, 'userDetails.accounts', []);
  const selectedAccount = find(accounts, 'selected');
  const roles = get(selectedAccount, 'roles', []);
  return roles.reduce((acc, value) => {
    acc.push(value.i18nKey);
    return acc;
  }, []);
});

const makeSelectUser = createSelector(selectUserDomain, (substate) => substate);

const makeSelectIsUserSubscribed = createSelector(
  selectUserTierDomain,
  (tier) => get(tier, 'stripePlanId', null) !== null,
);

const makeSelectIsUserCancelling = createSelector(
  selectUserTierDomain,
  (tier) => get(tier, 'cancelAtPeriodEnd', false) === true,
);

const makeSelectSubscriptionPlanName = createSelector(
  selectUserTierDomain,
  (tier) => get(tier, 'name', '').toLowerCase(),
);

const makeSelectStripePlanId = createSelector(selectUserTierDomain, (tier) =>
  get(tier, 'stripePlanId'),
);

const makeSelectUpcomingPlanStripeId = createSelector(
  selectUserTierDomain,
  (tier) => get(tier, 'upcoming.stripePlanId'),
);

const makeSelectUpcomingPlanStartDate = createSelector(
  selectUserTierDomain,
  (tier) => get(tier, 'upcoming.startAt'),
);

const makeSelectCurrentPlanEndDate = createSelector(
  selectUserTierDomain,
  (tier) => get(tier, 'currentPeriodEnd'),
);

const makeSelectUserType = createSelector(
  makeSelectUserIsPublisher,
  makeSelectIsPollfishAdmin,
  (isPublisher, isPollfishAdmin) => {
    let userType = USER_RESEARCHER;
    if (isPublisher) {
      userType = USER_PUBLISHER;
    }

    if (isPollfishAdmin) {
      userType = USER_ADMIN;
    }

    return userType;
  },
);
const makeSelectIsUserGuest = createSelector(selectUserDomain, (substate) =>
  get(substate, 'userDetails.isGuest', false),
);

const makeSelectedUserFlexCompletes = createSelector(
  selectUserDomain,
  (substate) => {
    const flexibleCompletes = get(
      substate,
      'userDetails.perkMetadata.flexibleCompletes',
      {},
    );
    return flexibleCompletes;
  },
);

const makeSelectUserHasFetched = createSelector(
  selectUserDomain,
  (substate) => substate.hasFetched,
);

const makeSelectUserCrunchStatus = createSelector(
  selectUserDomain,
  (substate) => get(substate, 'userDetails.userCrunchStatus', ''),
);

const makeSelectUserDLinkProdegePanelRedirects = createSelector(
  makeSelectAccountPerks,
  (perkMetadata) => {
    const dLinkProdegePanelRedirectsPerk = get(
      perkMetadata,
      'dLinkProdegePanelRedirects',
      false,
    );
    return dLinkProdegePanelRedirectsPerk;
  },
);

const makeSelectUserHiddenVariables = createSelector(
  makeSelectAccountPerks,
  (perkMetadata) => get(perkMetadata, 'hiddenVariables', false),
);

const makeSelectedHideAgeUnder18Perk = createSelector(
  makeSelectAccountPerks,
  (perkMetadata) => get(perkMetadata, 'hideAgesUnder18', false),
);

const makeIsSelectedAccountSubWithCredits = createSelector(
  makeSelectUserAccountSelected,
  (selectedUser = {}) => {
    const isSubAccount = get(selectedUser, 'isSubAccount', false);
    const { isExpired, sharedResources } = selectedUser.masterAccount || {};
    return (
      isSubAccount &&
      sharedResources.includes(SHARED_RESOURCES.CREDITS) &&
      !isExpired
    );
  },
);

const makeSelectUserEditLiveQuestionnaire = createSelector(
  makeSelectAccountPerks,
  (perkMetadata) => get(perkMetadata, 'editLiveQuestionnaire', false),
);

const makeSelectUserAudienceTemplates = createSelector(
  makeSelectAccountPerks,
  (perkMetadata) => get(perkMetadata, 'audienceTemplates', false),
);

const makeSelectUserForceDlinkTerms = createSelector(
  makeSelectAccountPerks,
  (perkMetadata) => get(perkMetadata, 'forceDlinkTerms', false),
);

const makeSelectUserQuestionnaireSimpleQuotas = createSelector(
  makeSelectAccountPerks,
  (perkMetadata) => get(perkMetadata, 'questionnaireSimpleQuotas', false),
);

const makeSelectUserQuestionnaireMinimumResponses = createSelector(
  makeSelectAccountPerks,
  (perkMetadata) => get(perkMetadata, 'questionnaireMinimumResponses', false),
);

const makeSelectUserQuestionnaireAdvancedQuotas = createSelector(
  makeSelectAccountPerks,
  (perkMetadata) => get(perkMetadata, 'questionnaireAdvancedQuotas', false),
);

export default makeSelectUser;
export {
  makeSelectUserWhiteLabelling,
  makeSelectUserBigQuery,
  makeSelectUserTeamAccountSeats,
  makeSelectUserTranslations,
  makeSelectUserDownloadSummary,
  makeSelectUserAiReport,
  makeSelectUserAccounts,
  makeSelectUserAccountSelected,
  makeSelectUserDetails,
  makeSelectIsPollfishAdmin,
  makeSelectIsUserGuest,
  makeSelectAccountHasAdvertisingId,
  makeSelectedAccountHasMobileOsPerk,
  makeSelectedAccountHasThirdParty,
  makeSelectUser,
  selectUserDomain,
  makeSelectIsUserSubscribed,
  makeSelectIsUserCancelling,
  makeSelectSubscriptionPlanName,
  makeSelectStripePlanId,
  makeSelectUpcomingPlanStripeId,
  makeSelectUpcomingPlanStartDate,
  makeSelectCurrentPlanEndDate,
  makeSelectUserRoles,
  makeSelectUserEmail,
  makeSelectUserIsPublisher,
  makeSelectPublisherDetails,
  makeSelectedUserFlexCompletes,
  makeSelectUserType,
  makeSelectHasPerk,
  makeSelectUserAccountEmails,
  makeSelectUserHasFetched,
  makeSelectedAccountHasDistributionLink,
  makeSelectedAccountHasTemplates,
  makeSelectBilling,
  makeSelectAccountPerks,
  makeSelectIsProdegeUser,
  makeSelectUserCrunchStatus,
  makeSelectedAccountHasRegularSurvey,
  makeSelectedAccountHasPeeqSurvey,
  makeSelectedAccountHasSettingIrAndLoi,
  makeSelectUserDLinkProdegePanelRedirects,
  makeSelectUserHiddenVariables,
  makeSelectedHideAgeUnder18Perk,
  makeIsSelectedAccountSubWithCredits,
  makeSelectUserEditLiveQuestionnaire,
  makeSelectUserAudienceTemplates,
  makeSelectUserForceDlinkTerms,
  makeSelectUserQuestionnaireSimpleQuotas,
  makeSelectUserQuestionnaireMinimumResponses,
  makeSelectUserQuestionnaireAdvancedQuotas,
};
